import api from './api';

// ============ Types ============

export interface StepShowWhen {
  step_id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_empty' | 'is_empty';
  value?: any;
}

export interface DynamicStep {
  id: string;
  type: 'form_fields' | 'tribunal_selection' | 'topics' | 'requests';
  title: string;
  description?: string;
  icon?: string;
  order: number;
  enabled: boolean;
  ai_hint?: string;
  show_when?: StepShowWhen | null;
  tips: string[];
  config: Record<string, any>;
}

export interface DynamicStepsConfig {
  steps: DynamicStep[];
}

export interface StepField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  description?: string;
  options?: { value: string; label: string }[];
  allow_other?: boolean;
  other_label?: string;
  other_type?: 'text' | 'textarea';
  minLength?: number;
  maxLength?: number;
  format?: string;
  order: number;
  show_when?: {
    field: string;
    operator: string;
    value?: any;
  };
  subfields?: StepField[];
}

/**
 * Legacy StepsConfig (old fixed-key format).
 * Kept for backward compat -- migrated at runtime.
 */
export interface LegacyStepsConfig {
  case_description?: {
    enabled: boolean;
    title: string;
    description?: string;
    allow_file_upload: boolean;
    allow_audio: boolean;
    fields: StepField[];
  };
  tribunal_selection?: {
    enabled: boolean;
    title: string;
    show_action_type: boolean;
    show_rite: boolean;
    show_jurisdiction: boolean;
    predefined_tribunals: string[];
    predefined_actions: string[];
  };
  topics?: {
    enabled: boolean;
    title: string;
    predefined_topics: { id: string; label: string; group?: string }[];
    allow_custom_topics: boolean;
  };
  requests?: {
    enabled: boolean;
    title: string;
    predefined_requests: { titulo: string; descricao: string; baseLegal?: string }[];
    allow_custom_requests: boolean;
  };
}

export type StepsConfig = DynamicStepsConfig | LegacyStepsConfig;

export interface DocumentFlow {
  id: string;
  name: string;
  slug: string;
  description?: string;
  category: string;
  area: string;
  areas: string[];
  icon?: string;
  version: number;
  status: string;
  is_featured: boolean;
  display_order: number;
  is_approved: boolean;
  approved_at?: string;
  approved_by?: string;
  steps_config: DynamicStepsConfig;
  ai_context?: string;
  ai_prompt_hints?: string;
  ai_system_instructions?: string;
  ai_example_output?: string;
  ai_document_structure?: string;
  ai_prefill_enabled: boolean;
  ai_prefill_instructions?: string;
  ai_upload_analysis_enabled: boolean;
  ai_upload_analysis_instructions?: string;
  generation_mode: string;
  ai_validation_enabled: boolean;
  ai_temperature_preset: string;
  ai_max_tokens: number;
  ai_context_extraction_focus?: string;
  default_writing_style: string;
  credit_cost: number;
  usage_count: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface FlowDraft {
  id: string;
  user_id: string;
  flow_id: string;
  flow_version: number;
  current_step: number;
  current_step_id?: string;
  form_data: Record<string, any>;
  uploaded_file_refs?: string[];
  created_at: string;
  updated_at: string;
  flow_name?: string;
  flow_slug?: string;
  flow_icon?: string;
  total_steps?: number;
}

export interface FlowAuditLog {
  id: string;
  flow_id: string;
  user_id: string;
  action: string;
  changes?: Record<string, any>;
  created_at: string;
  user_name?: string;
}

export interface FlowAnalytics {
  flow_id: string;
  total_uses: number;
  total_drafts: number;
  total_favorites: number;
  completion_rate?: number;
  avg_steps_completed?: number;
}

// ============ AI Suggestion Types ============

export interface FlowSuggestion {
  type: 'missing_field' | 'missing_step' | 'ai_hint';
  step_id?: string;
  message: string;
  suggested_data?: any;
}

export interface PromptPreview {
  system_prompt: string;
  user_prompt: string;
  estimated_tokens: number;
  estimated_cost_credits: number;
}

// ============ Migration Utility ============

const LEGACY_KEY_META: Record<string, { type: DynamicStep['type']; defaultTitle: string; defaultIcon: string }> = {
  case_description: { type: 'form_fields', defaultTitle: 'Descricao do Caso', defaultIcon: 'description' },
  tribunal_selection: { type: 'tribunal_selection', defaultTitle: 'Tribunal e Tipo de Acao', defaultIcon: 'gavel' },
  topics: { type: 'topics', defaultTitle: 'Topicos Juridicos', defaultIcon: 'topic' },
  requests: { type: 'requests', defaultTitle: 'Pedidos', defaultIcon: 'request_quote' },
};

export function migrateStepsConfig(config: any): DynamicStepsConfig {
  if (!config) return { steps: [] };
  if (config.steps && Array.isArray(config.steps)) return config as DynamicStepsConfig;

  const steps: DynamicStep[] = [];
  let order = 0;

  for (const [key, meta] of Object.entries(LEGACY_KEY_META)) {
    const stepData = (config as any)[key];
    if (!stepData || typeof stepData !== 'object') continue;
    if (!stepData.enabled) continue;

    const { enabled: _e, title: _t, description: _d, ...restConfig } = stepData;

    steps.push({
      id: `legacy_${key}`,
      type: meta.type,
      title: stepData.title || meta.defaultTitle,
      description: stepData.description || undefined,
      icon: meta.defaultIcon,
      order,
      enabled: true,
      ai_hint: undefined,
      show_when: null,
      tips: [],
      config: restConfig,
    });
    order++;
  }

  return { steps };
}

function ensureMigrated(flow: any): any {
  if (flow && flow.steps_config) {
    flow.steps_config = migrateStepsConfig(flow.steps_config);
  }
  return flow;
}

function ensureMigratedList(flows: any[]): any[] {
  return flows.map(f => ensureMigrated(f));
}

// ============ Admin CRUD ============

export const listFlows = async (params?: {
  category?: string;
  area?: string;
  status?: string;
  search?: string;
  show_disabled?: boolean;
  only_approved?: boolean;
  skip?: number;
  limit?: number;
}): Promise<{ flows: DocumentFlow[]; total: number }> => {
  const response = await api.get('/document-flows/', { params });
  return { ...response.data, flows: ensureMigratedList(response.data.flows) };
};

export const getFlow = async (flowId: string): Promise<DocumentFlow> => {
  const response = await api.get(`/document-flows/${flowId}`);
  return ensureMigrated(response.data);
};

export const createFlow = async (data: Partial<DocumentFlow>): Promise<DocumentFlow> => {
  const response = await api.post('/document-flows/', data);
  return ensureMigrated(response.data);
};

export const updateFlow = async (flowId: string, data: Partial<DocumentFlow>): Promise<DocumentFlow> => {
  const response = await api.put(`/document-flows/${flowId}`, data);
  const updated = ensureMigrated(response.data);
  invalidateFlowCache(updated.slug);
  return updated;
};

export const deleteFlow = async (flowId: string): Promise<void> => {
  await api.delete(`/document-flows/${flowId}`);
};

export const duplicateFlow = async (flowId: string): Promise<DocumentFlow> => {
  const response = await api.post(`/document-flows/${flowId}/duplicate`);
  return ensureMigrated(response.data);
};

export const updateFlowStatus = async (flowId: string, status: string): Promise<DocumentFlow> => {
  const response = await api.patch(`/document-flows/${flowId}/status`, { status });
  return ensureMigrated(response.data);
};

export const updateFlowApproval = async (flowId: string, isApproved: boolean): Promise<DocumentFlow> => {
  const response = await api.patch(`/document-flows/${flowId}/approval`, { is_approved: isApproved });
  return ensureMigrated(response.data);
};

// ============ Analytics & Audit ============

export const getFlowAnalytics = async (flowId: string): Promise<FlowAnalytics> => {
  const response = await api.get(`/document-flows/${flowId}/analytics`);
  return response.data;
};

export const getFlowAuditLog = async (flowId: string, params?: {
  skip?: number;
  limit?: number;
}): Promise<{ logs: FlowAuditLog[]; total: number }> => {
  const response = await api.get(`/document-flows/${flowId}/audit-log`, { params });
  return response.data;
};

// ============ Public / User ============

export const listActiveFlows = async (params?: {
  category?: string;
  area?: string;
  search?: string;
}): Promise<DocumentFlow[]> => {
  const response = await api.get('/document-flows/active/list', { params });
  return ensureMigratedList(response.data);
};

const _flowCache = new Map<string, { data: DocumentFlow; ts: number }>();
const _prefetchInFlight = new Map<string, Promise<DocumentFlow>>();
const CACHE_TTL_MS = 60_000;

export const invalidateFlowCache = (slug?: string): void => {
  if (slug) {
    _flowCache.delete(slug);
    _prefetchInFlight.delete(slug);
  } else {
    _flowCache.clear();
    _prefetchInFlight.clear();
  }
};

export const getFlowBySlug = async (slug: string): Promise<DocumentFlow> => {
  const cached = _flowCache.get(slug);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.data;

  const inflight = _prefetchInFlight.get(slug);
  if (inflight) return inflight;

  const promise = api.get(`/document-flows/by-slug/${slug}/config`).then(r => {
    const flow = ensureMigrated(r.data);
    _flowCache.set(slug, { data: flow, ts: Date.now() });
    _prefetchInFlight.delete(slug);
    return flow;
  });
  _prefetchInFlight.set(slug, promise);
  return promise;
};

export const prefetchFlow = (slug: string): void => {
  if (_flowCache.has(slug) || _prefetchInFlight.has(slug)) return;
  getFlowBySlug(slug).catch(() => {});
};

export const getRecentFlows = async (limit?: number): Promise<DocumentFlow[]> => {
  const response = await api.get('/document-flows/recent/list', { params: { limit } });
  return ensureMigratedList(response.data);
};

// ============ Favorites ============

export const addFavorite = async (flowId: string): Promise<void> => {
  await api.post(`/document-flows/${flowId}/favorite`);
};

export const removeFavorite = async (flowId: string): Promise<void> => {
  await api.delete(`/document-flows/${flowId}/favorite`);
};

export const listFavorites = async (): Promise<DocumentFlow[]> => {
  const response = await api.get('/document-flows/favorites/list');
  return response.data;
};

// ============ Drafts ============

export const listDrafts = async (params?: {
  flow_id?: string;
  skip?: number;
  limit?: number;
}): Promise<{ drafts: FlowDraft[]; total: number }> => {
  const response = await api.get('/document-flows/drafts/list', { params });
  return response.data;
};

export const getDraft = async (draftId: string): Promise<FlowDraft> => {
  const response = await api.get(`/document-flows/drafts/${draftId}`);
  return response.data;
};

export const createDraft = async (flowId: string, data?: {
  current_step?: number;
  current_step_id?: string;
  form_data?: Record<string, any>;
  uploaded_file_refs?: string[];
}): Promise<FlowDraft> => {
  const response = await api.post(`/document-flows/${flowId}/drafts`, data || {});
  return response.data;
};

export const updateDraft = async (draftId: string, data: {
  current_step?: number;
  current_step_id?: string;
  form_data?: Record<string, any>;
  uploaded_file_refs?: string[];
}): Promise<FlowDraft> => {
  const response = await api.put(`/document-flows/drafts/${draftId}`, data);
  return response.data;
};

export const deleteDraft = async (draftId: string): Promise<void> => {
  await api.delete(`/document-flows/drafts/${draftId}`);
};

// ============ Generation ============

export const generateDocument = async (flowId: string, data: {
  form_data: Record<string, any>;
  document_name?: string;
  depth_profile?: string;
  writing_style?: string;
  uploaded_file_refs?: string[];
  folder_id?: string;
  extracted_context?: string;
}): Promise<any> => {
  const response = await api.post(`/document-flows/${flowId}/generate`, data);
  return response.data;
};

export const estimateCredits = async (flowId: string, data: {
  form_data: Record<string, any>;
  depth_profile?: string;
}): Promise<{ estimated_credits: number }> => {
  const response = await api.post(`/document-flows/${flowId}/estimate-credits`, data);
  return response.data;
};

// ============ AI Prefill & Upload Analysis ============

export const prefillFromDocument = async (flowId: string, rawText: string): Promise<{
  extracted_data: string;
  suggested_fields: Record<string, any>;
  fields_count: number;
  credits_charged: number;
}> => {
  const response = await api.post(`/document-flows/${flowId}/prefill`, { raw_text: rawText });
  return response.data;
};

export const analyzeUploads = async (flowId: string, rawText: string): Promise<{
  analysis: string;
  credits_charged: number;
}> => {
  const response = await api.post(`/document-flows/${flowId}/analyze-uploads`, { raw_text: rawText });
  return response.data;
};

export const uploadFlowFile = async (file: File): Promise<{
  file_id: string;
  filename: string;
  size: number;
  type: string;
  mime_type?: string;
  transcription?: string;
  extracted_text?: string;
  preview_url?: string;
}> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/chat/attachments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getFlowAttachmentMetadata = async (fileIds: string[]): Promise<Array<{
  file_id: string;
  filename: string;
  size: number;
  type: string;
  mime_type?: string;
  transcription?: string;
  extracted_text?: string;
  preview_url?: string;
}>> => {
  if (!fileIds.length) return [];
  const response = await api.get('/chat/attachments/metadata/batch', {
    params: { ids: fileIds.join(',') },
  });
  return response.data.items || [];
};

// ============ Regeneration & Context Reuse ============

export const regenerateDocument = async (documentId: string, data?: {
  form_data?: Record<string, any>;
  document_name?: string;
  depth_profile?: string;
  writing_style?: string;
}): Promise<any> => {
  const response = await api.post(`/document-flows/regenerate-document/${documentId}`, data || { form_data: {} });
  return response.data;
};

export const getDocumentContext = async (documentId: string): Promise<{
  document_id: string;
  title: string;
  flow_id?: string;
  flow_slug?: string;
  source?: string;
  form_data: Record<string, any>;
  content_preview: string;
  generation_context?: Record<string, any>;
  created_at?: string;
}> => {
  const response = await api.get(`/document-flows/document-context/${documentId}`);
  return response.data;
};

// ============ Legislation ============

export interface LegislationArticle {
  id: string;
  law_name: string;
  law_code: string;
  article_number: string;
  article_text: string;
  area: string;
  topics?: string[];
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const listLegislation = async (params?: {
  law_code?: string;
  area?: string;
  search?: string;
  active_only?: boolean;
  skip?: number;
  limit?: number;
}): Promise<{ articles: LegislationArticle[]; total: number }> => {
  const response = await api.get('/legislation/', { params });
  return response.data;
};

export const createLegislation = async (data: Partial<LegislationArticle>): Promise<LegislationArticle> => {
  const response = await api.post('/legislation/', data);
  return response.data;
};

export const updateLegislation = async (id: string, data: Partial<LegislationArticle>): Promise<LegislationArticle> => {
  const response = await api.put(`/legislation/${id}`, data);
  return response.data;
};

export const deleteLegislation = async (id: string): Promise<void> => {
  await api.delete(`/legislation/${id}`);
};

export const seedLegislation = async (): Promise<{ seeded: number }> => {
  const response = await api.post('/legislation/seed');
  return response.data;
};

// ============ AI Admin Tools ============

export const suggestFields = async (flowId: string, data: {
  flow_name: string;
  area: string;
  category: string;
  steps: DynamicStep[];
}): Promise<{ suggestions: FlowSuggestion[] }> => {
  const response = await api.post(`/document-flows/${flowId}/suggest-fields`, data);
  return response.data;
};

export const generateFlowFromDescription = async (data: {
  description: string;
  area: string;
  category: string;
}): Promise<Partial<DocumentFlow>> => {
  const response = await api.post('/document-flows/generate-from-description', data);
  return ensureMigrated(response.data);
};

export const previewPrompt = async (flowId: string, data?: {
  sample_data?: Record<string, any>;
}): Promise<PromptPreview> => {
  const response = await api.post(`/document-flows/${flowId}/preview-prompt`, data || {});
  return response.data;
};

export const getRecentTribunals = async (limit?: number): Promise<string[]> => {
  const response = await api.get('/document-flows/user/recent-tribunals', { params: { limit } });
  return response.data;
};

// ============ Step Templates (frontend only) ============

export interface StepTemplate {
  name: string;
  description: string;
  icon: string;
  step: Partial<DynamicStep>;
}

export const STEP_TEMPLATES: StepTemplate[] = [
  {
    name: 'Descricao do Caso',
    description: 'Textarea para fatos, com opcao de upload e audio',
    icon: 'description',
    step: {
      type: 'form_fields',
      title: 'Descricao do Caso',
      icon: 'description',
      ai_hint: 'Use os fatos descritos para compor a secao DOS FATOS da peticao',
      tips: ['Descreva os fatos em ordem cronologica', 'Inclua datas, valores e nomes completos'],
      config: {
        allow_file_upload: true,
        allow_audio: true,
        ai_prefill_enabled: true,
        fields: [
          { id: 'descricao_fatos', label: 'Descricao dos Fatos', type: 'textarea', required: true, minLength: 200, order: 0 },
        ],
      },
    },
  },
  {
    name: 'Dados do Autor',
    description: 'Nome, CPF/CNPJ, endereco, telefone, email',
    icon: 'person',
    step: {
      type: 'form_fields',
      title: 'Dados do Autor',
      icon: 'person',
      ai_hint: 'Use estes dados para a QUALIFICACAO DO AUTOR no documento',
      config: {
        fields: [
          { id: 'nome_autor', label: 'Nome Completo', type: 'text', required: true, order: 0 },
          { id: 'cpf_autor', label: 'CPF/CNPJ', type: 'text', required: false, format: 'cpf', order: 1 },
          { id: 'endereco_autor', label: 'Endereco', type: 'text', required: false, order: 2 },
          { id: 'telefone_autor', label: 'Telefone', type: 'text', required: false, format: 'phone', order: 3 },
          { id: 'email_autor', label: 'Email', type: 'text', required: false, format: 'email', order: 4 },
        ],
      },
    },
  },
  {
    name: 'Dados do Reu',
    description: 'Nome, CPF/CNPJ, endereco do reu',
    icon: 'person_outline',
    step: {
      type: 'form_fields',
      title: 'Dados do Reu',
      icon: 'person_outline',
      ai_hint: 'Use estes dados para a QUALIFICACAO DO REU no documento',
      config: {
        fields: [
          { id: 'nome_reu', label: 'Nome Completo', type: 'text', required: true, order: 0 },
          { id: 'cpf_reu', label: 'CPF/CNPJ', type: 'text', required: false, format: 'cnpj', order: 1 },
          { id: 'endereco_reu', label: 'Endereco', type: 'text', required: false, order: 2 },
        ],
      },
    },
  },
  {
    name: 'Valor da Causa',
    description: 'Campo de valor monetario e justificativa',
    icon: 'attach_money',
    step: {
      type: 'form_fields',
      title: 'Valor da Causa',
      icon: 'attach_money',
      ai_hint: 'Use o valor da causa para o enderecamento e calculo de custas',
      config: {
        fields: [
          { id: 'valor_causa', label: 'Valor da Causa (R$)', type: 'currency', required: true, order: 0 },
          { id: 'justificativa_valor', label: 'Justificativa do Valor', type: 'textarea', required: false, order: 1 },
        ],
      },
    },
  },
  {
    name: 'Provas e Documentos',
    description: 'Upload de arquivos e lista de provas',
    icon: 'folder',
    step: {
      type: 'form_fields',
      title: 'Provas e Documentos',
      icon: 'folder',
      ai_hint: 'Referencie as provas e documentos mencionados na argumentacao',
      config: {
        allow_file_upload: true,
        fields: [
          { id: 'lista_provas', label: 'Lista de Provas', type: 'textarea', required: false, order: 0 },
        ],
      },
    },
  },
  {
    name: 'Dados do Contrato',
    description: 'Numero, data, partes, objeto, valor do contrato',
    icon: 'article',
    step: {
      type: 'form_fields',
      title: 'Dados do Contrato',
      icon: 'article',
      ai_hint: 'Use os dados do contrato como base factual para a argumentacao',
      config: {
        fields: [
          { id: 'numero_contrato', label: 'Numero do Contrato', type: 'text', required: false, order: 0 },
          { id: 'data_contrato', label: 'Data do Contrato', type: 'text', required: false, format: 'date_br', order: 1 },
          { id: 'partes_contrato', label: 'Partes', type: 'textarea', required: false, order: 2 },
          { id: 'objeto_contrato', label: 'Objeto', type: 'textarea', required: true, order: 3 },
          { id: 'valor_contrato', label: 'Valor (R$)', type: 'currency', required: false, order: 4 },
        ],
      },
    },
  },
  {
    name: 'Etapa Vazia',
    description: 'Comece do zero com campos personalizados',
    icon: 'add_box',
    step: {
      type: 'form_fields',
      title: 'Nova Etapa',
      icon: 'edit_note',
      config: { fields: [] },
    },
  },
];

// ============ New endpoints for flow suggestions ============

export interface FlowSuggestion {
  id: string;
  name: string;
  slug: string;
  description?: string;
  area: string;
  icon?: string;
  is_featured?: boolean;
  is_approved?: boolean;
  _detected_area?: string;  // Area detected from context by the AI
}

export const getSuggestedFlows = async (context: string, limit: number = 5): Promise<FlowSuggestion[]> => {
  try {
    const response = await api.get('/document-flows/suggest', {
      params: { context, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching suggested flows:', error);
    return [];
  }
};

export const getFrequentFlows = async (limit: number = 5): Promise<FlowSuggestion[]> => {
  try {
    const response = await api.get('/document-flows/user/frequent', {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching frequent flows:', error);
    return [];
  }
};
